// Chairman briefing per-KPI chart.
//
// Renders ONLY the TODAY/TARGET stacked-bar hero + legend — none of the
// row-commitment ledger that lives on /supply and /demand. The full
// charts on those pages are interactive (click-to-commit); the chairman
// briefing is read-only and dense.
//
// Data sources per KPI (matched to what each Supply/Demand chart uses):
//   S1 → SIGNUP_BONUS_SOURCE + cat.earnTodayPct / cat.intent.earnTargetPct
//        (live override via live-spend-share-by-category not applicable —
//         that query is spend-side. Earn stays static.)
//   D1 → cat.spendTodayPct / cat.intent.spendTargetPct, with live override
//        from live-spend-share-by-category when available.
//   S2 → derived cadence-bucket distribution (daily / weekly / monthly /
//        below-threshold) computed from each category's `cadence` field
//        weighted by earn target share. Mirrors the source briefing's
//        S2 chart shape.
//   D2 → cat.retention.todayPct / cat.retention.targetPct per category
//        (not a sum-to-100 distribution; rendered as a side-by-side bar
//        per category showing today vs target retention).
//   S3 → consigned vs Bvndle-funded mix (today vs target) — one bar pair
//        showing the share of issuance that's pre-funded by partners.
//   D3 → settlement latency (today vs target days) per category — small
//        bar pair per category, longer = slower.
//
// Sizing: this is meant to fit in the LEFT cell of row 2 of the KPI
// table, which is ~62% of the briefing page width. Heights stay short
// (~70-100px) so the whole KPI block fits 2-3 per A4 page.

import { useLiveCategoryStats, toSharePct } from "../../lib/live-category-stats";
import { CATEGORIES, SIGNUP_BONUS_SOURCE } from "../../lib/categories";

interface Segment {
  label: string;
  pct: number;
  color: string;
}

interface ChartData {
  today: Segment[];
  target: Segment[];
}

// Stable colour palette — same hex sequence the Supply/Demand DistributionChart
// uses (preserves visual continuity between surfaces).
const CHART_COLORS = [
  "#b8b8b8", // signup-bonus / "other" — neutral
  "#5ec98c", // greens
  "#7fd1a8",
  "#4fb3ff", // blues
  "#7ec7e8",
  "#f5c47e", // ambers
  "#d99c3a",
  "#c44a4a", // reds
  "#8c5e2b", // browns
  "#a07a52",
];

export function ChairmanKpiChart({ kpiCode }: { kpiCode: string }) {
  const liveSpend = useLiveCategoryStats(
    kpiCode === "D1" ? "live-spend-share-by-category" : "",
  );
  const liveSpendShare = toSharePct(liveSpend.byCode);

  let data: ChartData | null = null;
  let legend: Segment[] = [];
  let mode: "stacked" | "paired" = "stacked";

  if (kpiCode === "S1") {
    data = buildEarnDistribution();
    legend = data.target;
  } else if (kpiCode === "D1") {
    data = buildSpendDistribution(liveSpendShare);
    legend = data.target;
  } else if (kpiCode === "S2") {
    data = buildCadenceDistribution();
    legend = data.target;
  } else if (kpiCode === "S3") {
    data = buildConsignedMix();
    legend = data.target;
  } else if (kpiCode === "D2") {
    return <PairedBars data={buildRetention()} unit="%" />;
  } else if (kpiCode === "D3") {
    return <PairedBars data={buildSettlement()} unit="d" />;
  }

  if (!data) return null;
  void mode;

  return (
    <div style={wrap}>
      <BarRow label="TODAY" segments={data.today} />
      <BarRow label="TARGET" segments={data.target} />
      <Legend items={legend} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Per-KPI data builders
// ---------------------------------------------------------------------------

function buildEarnDistribution(): ChartData {
  // Today: ~98% signup_bonus + tiny slivers across categories. Target:
  // spread across the must-win categories with signup_bonus capped at 10%.
  const segments: Array<{ code: string; label: string; today: number; target: number }> = [
    {
      code: SIGNUP_BONUS_SOURCE.code,
      label: "Signup bonus",
      today: SIGNUP_BONUS_SOURCE.earnTodayPct,
      target: SIGNUP_BONUS_SOURCE.earnTargetPct,
    },
  ];
  let otherToday = 0;
  for (const c of CATEGORIES) {
    const today = c.earnTodayPct ?? 0;
    const target = c.intent?.earnTargetPct ?? 0;
    if (today === 0 && target === 0) continue;
    segments.push({ code: c.code, label: shortLabel(c.label), today, target });
    otherToday += today;
  }
  // Filler to round TODAY to ~100
  const todayTotal = segments.reduce((s, x) => s + x.today, 0);
  if (todayTotal < 100) {
    segments.push({
      code: "other",
      label: "Other",
      today: Math.max(0, 100 - todayTotal),
      target: 0,
    });
  }
  void otherToday;
  return packSegments(segments);
}

function buildSpendDistribution(liveByCode: Record<string, number>): ChartData {
  const segments: Array<{ code: string; label: string; today: number; target: number }> = [];
  const hasLive = Object.keys(liveByCode).length > 0;
  for (const c of CATEGORIES) {
    const today = hasLive
      ? liveByCode[c.code] ?? 0
      : c.spendTodayPct ?? 0;
    const target = c.intent?.spendTargetPct ?? 0;
    if (today === 0 && target === 0) continue;
    segments.push({ code: c.code, label: shortLabel(c.label), today, target });
  }
  // Today filler if classifier hasn't caught everything
  const todayTotal = segments.reduce((s, x) => s + x.today, 0);
  if (todayTotal < 99) {
    segments.push({
      code: "other",
      label: "Other",
      today: Math.max(0, 100 - todayTotal),
      target: 0,
    });
  }
  return packSegments(segments);
}

function buildCadenceDistribution(): ChartData {
  // Group categories by cadence; sum earn% per bucket.
  const buckets = { daily: 0, weekly: 0, monthly: 0, occasional: 0 };
  const targets = { daily: 0, weekly: 0, monthly: 0, occasional: 0 };
  for (const c of CATEGORIES) {
    const cadence = c.intent?.cadence;
    if (!cadence || cadence === "n/a") continue;
    const today = c.earnTodayPct ?? 0;
    const target = c.intent?.earnTargetPct ?? 0;
    if (cadence === "daily") {
      buckets.daily += today; targets.daily += target;
    } else if (cadence === "weekly") {
      buckets.weekly += today; targets.weekly += target;
    } else if (cadence === "monthly") {
      buckets.monthly += today; targets.monthly += target;
    } else {
      buckets.occasional += today; targets.occasional += target;
    }
  }
  // "Below threshold" = remainder so the bar sums to ~100
  const todayUsed = buckets.daily + buckets.weekly + buckets.monthly + buckets.occasional;
  const targetUsed = targets.daily + targets.weekly + targets.monthly + targets.occasional;
  const segs = [
    { code: "daily", label: "Daily-cadence", today: buckets.daily, target: targets.daily },
    { code: "weekly", label: "Weekly", today: buckets.weekly, target: targets.weekly },
    { code: "monthly", label: "Monthly", today: buckets.monthly, target: targets.monthly },
    { code: "below", label: "Below threshold",
      today: Math.max(0, 100 - todayUsed),
      target: Math.max(0, 100 - targetUsed) },
  ];
  return packSegments(segs);
}

function buildConsignedMix(): ChartData {
  // Single bar pair: % of issuance pre-funded (consigned) vs Bvndle-funded.
  let todayConsigned = 0;
  let targetConsigned = 0;
  let totalWeight = 0;
  for (const c of CATEGORIES) {
    const earn = c.intent?.earnTargetPct ?? 0;
    if (earn === 0) continue;
    totalWeight += earn;
    const today = c.consignment?.consignedTodayPct ?? 0;
    const target = c.consignment?.consignedTargetPct ?? 0;
    todayConsigned += (today / 100) * earn;
    targetConsigned += (target / 100) * earn;
  }
  const todayC = totalWeight > 0 ? (todayConsigned / totalWeight) * 100 : 0;
  const targetC = totalWeight > 0 ? (targetConsigned / totalWeight) * 100 : 0;
  const segs = [
    { code: "consigned", label: "Consigned · partner-funded",
      today: todayC, target: targetC },
    { code: "bvndle", label: "Bvndle-funded",
      today: 100 - todayC, target: 100 - targetC },
  ];
  return packSegments(segs);
}

interface PairedRow {
  label: string;
  today: number;
  target: number;
  unit: "%" | "d";
  color: string;
}

function buildRetention(): PairedRow[] {
  return CATEGORIES
    .filter((c) => c.retention)
    .slice(0, 6)
    .map((c, i) => ({
      label: shortLabel(c.label),
      today: c.metrics?.retain14dPct ?? 0,
      target: c.retention!.targetPct,
      unit: "%" as const,
      color: CHART_COLORS[(i + 1) % CHART_COLORS.length]!,
    }));
}

function buildSettlement(): PairedRow[] {
  return CATEGORIES
    .filter((c) => c.settlement)
    .slice(0, 6)
    .map((c, i) => ({
      label: shortLabel(c.label),
      today: c.settlement!.latencyTodayDays,
      target: c.settlement!.latencyTargetDays,
      unit: "d" as const,
      color: CHART_COLORS[(i + 1) % CHART_COLORS.length]!,
    }));
}

// ---------------------------------------------------------------------------
// Pack raw {code,label,today,target} into Segment[] with assigned colours.
// ---------------------------------------------------------------------------
function packSegments(
  rows: Array<{ code: string; label: string; today: number; target: number }>,
): ChartData {
  const today: Segment[] = [];
  const target: Segment[] = [];
  rows.forEach((r, i) => {
    const color = CHART_COLORS[i % CHART_COLORS.length]!;
    today.push({ label: r.label, pct: r.today, color });
    target.push({ label: r.label, pct: r.target, color });
  });
  return { today, target };
}

function shortLabel(label: string): string {
  // "Retail · Supermarkets" → "Supermarkets"; "Automotive · Mobility" → "Auto/Mobility"
  return label
    .replace(/^Retail · /, "")
    .replace(/^Automotive · Mobility$/, "Auto/Mobility")
    .replace(/^E-Commerce · Digital Services$/, "E-commerce")
    .replace(/ & .*/, "")
    .replace(/ · .*/, "");
}

// ---------------------------------------------------------------------------
// Stacked TODAY/TARGET bar row
// ---------------------------------------------------------------------------
function BarRow({ label, segments }: { label: string; segments: Segment[] }) {
  const total = segments.reduce((s, x) => s + x.pct, 0);
  const visible = segments.filter((s) => s.pct > 0);
  return (
    <div style={barRow}>
      <span style={barRowLabel}>{label}</span>
      <div style={barTrack}>
        {visible.map((s, i) => {
          const w = total > 0 ? (s.pct / total) * 100 : 0;
          return (
            <div
              key={`${s.label}-${i}`}
              title={`${s.label}: ${s.pct.toFixed(1)}%`}
              style={{
                width: `${w}%`,
                background: s.color,
                minWidth: w > 0 ? 2 : 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "rgba(0,0,0,0.75)",
                fontSize: 9,
                fontWeight: 700,
                fontVariantNumeric: "tabular-nums",
                overflow: "hidden",
              }}
            >
              {w >= 7 ? `${Math.round(s.pct)}%` : ""}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Side-by-side paired bars (for D2 + D3 — per-category today vs target)
// ---------------------------------------------------------------------------
function PairedBars({ data, unit }: { data: PairedRow[]; unit: "%" | "d" }) {
  // Find max for scaling.
  const max = Math.max(...data.map((r) => Math.max(r.today, r.target)), 1);
  return (
    <div style={{ ...wrap, gap: 4 }}>
      {data.map((row) => (
        <div key={row.label} style={pairedRow}>
          <span style={pairedLabel}>{row.label}</span>
          <div style={pairedBars}>
            <div style={pairedBarOuter}>
              <span style={pairedBarTag}>TODAY</span>
              <div
                style={{
                  width: `${(row.today / max) * 100}%`,
                  background: row.color + "66",
                  height: 6,
                  borderRadius: 2,
                }}
              />
              <span style={pairedBarVal}>{row.today}{unit}</span>
            </div>
            <div style={pairedBarOuter}>
              <span style={pairedBarTag}>TARGET</span>
              <div
                style={{
                  width: `${(row.target / max) * 100}%`,
                  background: row.color,
                  height: 6,
                  borderRadius: 2,
                }}
              />
              <span style={pairedBarVal}>{row.target}{unit}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Legend
// ---------------------------------------------------------------------------
function Legend({ items }: { items: Segment[] }) {
  const visible = items.filter((s) => s.pct > 0 || s.label === "Other" || s.label === "Below threshold");
  return (
    <div style={legendWrap}>
      {visible.map((s, i) => (
        <span key={`${s.label}-${i}`} style={legendItem}>
          <span style={{ ...legendDot, background: s.color }} />
          {s.label}
        </span>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
import type { CSSProperties } from "react";

const wrap: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 5,
  padding: "6px 10px",
  background: "#fff",
};

const barRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
};

const barRowLabel: CSSProperties = {
  fontFamily: "SF Mono, Menlo, monospace",
  fontSize: 8,
  letterSpacing: "0.10em",
  fontWeight: 700,
  color: "#6B6F76",
  minWidth: 42,
  textAlign: "right",
};

const barTrack: CSSProperties = {
  display: "flex",
  width: "100%",
  height: 16,
  borderRadius: 2,
  overflow: "hidden",
  background: "transparent",
};

const legendWrap: CSSProperties = {
  display: "flex",
  flexWrap: "wrap",
  gap: "4px 12px",
  marginTop: 4,
  paddingLeft: 50,
  fontSize: 8.5,
  color: "#3A3A3A",
  fontFamily: "SF Mono, Menlo, monospace",
  letterSpacing: "0.04em",
};

const legendItem: CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
};

const legendDot: CSSProperties = {
  display: "inline-block",
  width: 8,
  height: 8,
  borderRadius: 2,
};

const pairedRow: CSSProperties = {
  display: "grid",
  gridTemplateColumns: "100px 1fr",
  gap: 6,
  alignItems: "center",
};

const pairedLabel: CSSProperties = {
  fontSize: 9,
  fontWeight: 600,
  color: "#1A1A1A",
};

const pairedBars: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
};

const pairedBarOuter: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
};

const pairedBarTag: CSSProperties = {
  fontFamily: "SF Mono, Menlo, monospace",
  fontSize: 7,
  letterSpacing: "0.12em",
  fontWeight: 700,
  color: "#6B6F76",
  minWidth: 36,
};

const pairedBarVal: CSSProperties = {
  fontSize: 9,
  fontVariantNumeric: "tabular-nums",
  color: "#3A3A3A",
  minWidth: 28,
};
